import React from "react";
import {
  Card,
  CardContent,
  CardHeader,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Chip,
  IconButton,
  Tooltip,
  Typography,
  Box,
  Link,
} from "@mui/material";
import {
  OpenInNew,
  CheckCircle,
  Schedule,
  RateReview,
} from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import {
  SubmissionReviewActivity,
  SubmissionReviewSubmission,
  SubmissionReviewStatus,
  ReviewAllocationRecord,
} from "../../types";
import { SubmissionDetail } from "./SubmissionDetail";

interface Props {
  activity: SubmissionReviewActivity;
  submissions: SubmissionReviewSubmission[];
  onRefresh?: () => void;
}

export function ParticipantsList({ activity, submissions, onRefresh }: Props) {
  const [t] = useTranslation();
  const [selectedSubmission, setSelectedSubmission] =
    React.useState<SubmissionReviewSubmission | null>(null);

  const showAllocations =
    activity.status !== SubmissionReviewStatus.Draft &&
    activity.status !== SubmissionReviewStatus.SubmissionsOpen;

  // Live presentations: rows are team presentations, not individual
  // submissions — there is no submission status to wait on.
  const isLive = activity.submissionType === "LIV";
  // Holistic team submissions label rows by team regardless of format.
  const isTeamRows = isLive || activity.submitterMode === "TM";

  return (
    <Card variant="outlined">
      <CardHeader
        title={`${String(t(isLive ? "Presentations" : "Participants"))} (${
          submissions.length
        })`}
        titleTypographyProps={{ variant: "h6" }}
      />
      <CardContent sx={{ p: 0, "&:last-child": { pb: 0 } }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>{t(isTeamRows ? "Team" : "Student")}</TableCell>
              {!isLive && <TableCell>{t("Submission")}</TableCell>}
              {activity.status === SubmissionReviewStatus.SubmissionsOpen && (
                <TableCell>{t("Link")}</TableCell>
              )}
              {showAllocations && <TableCell>{t("Reviewed By")}</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {submissions.map((submission) => {
              // Find allocations where this student is a reviewer
              const reviewingAllocations = submissions
                .flatMap((s) => s.allocations || [])
                .filter((a) => a.reviewerId === submission.authorId);

              // Allocations for this submission (who reviews this student's work)
              const reviewedByAllocations = submission.allocations || [];

              return (
                <TableRow key={submission.id}>
                  {/* Student name (clickable to open detail); team name for
                      live presentations */}
                  <TableCell>
                    <Link
                      component="button"
                      variant="body2"
                      onClick={() => setSelectedSubmission(submission)}
                      sx={{ cursor: "pointer", textDecoration: "none" }}
                    >
                      {isTeamRows
                        ? submission.groupName ?? submission.authorName
                        : submission.authorName}
                    </Link>
                  </TableCell>

                  {/* Submission status (not applicable to live presentations) */}
                  {!isLive && (
                    <TableCell>
                      <Chip
                        icon={
                          submission.status === "SB" ? (
                            <CheckCircle fontSize="small" />
                          ) : (
                            <Schedule fontSize="small" />
                          )
                        }
                        label={
                          submission.status === "SB"
                            ? String(t("Submitted"))
                            : String(t("Pending"))
                        }
                        color={
                          submission.status === "SB" ? "success" : "default"
                        }
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>
                  )}

                  {/* Submission link (only when submissions open) */}
                  {activity.status ===
                    SubmissionReviewStatus.SubmissionsOpen && (
                    <TableCell>
                      {submission.tokenizedLink && (
                        <Tooltip
                          title={String(
                            t("Open submission page as this student"),
                          )}
                        >
                          <IconButton
                            component="a"
                            href={submission.tokenizedLink}
                            target="_blank"
                            rel="noopener noreferrer"
                            size="small"
                          >
                            <OpenInNew fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                  )}

                  {/* Allocations: who is reviewing this student's submission */}
                  {showAllocations && (
                    <TableCell>
                      {reviewedByAllocations.length > 0 ? (
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 0.5,
                          }}
                        >
                          {reviewingAllocations.map((alloc) => {
                            const targetSubmission = submissions.find(
                              (s) => s.id === alloc.submissionId,
                            );
                            return (
                              <Box
                                key={alloc.id}
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 0.5,
                                }}
                              >
                                <Chip
                                  icon={
                                    alloc.reviewSubmitted ? (
                                      <CheckCircle fontSize="small" />
                                    ) : (
                                      <RateReview fontSize="small" />
                                    )
                                  }
                                  label={
                                    targetSubmission?.authorName ?? "Unknown"
                                  }
                                  size="small"
                                  color={
                                    alloc.reviewSubmitted
                                      ? "success"
                                      : "warning"
                                  }
                                  variant="outlined"
                                />
                                {alloc.reviewLink && (
                                  <Tooltip
                                    title={String(
                                      t("Open review as this student"),
                                    )}
                                  >
                                    <IconButton
                                      component="a"
                                      href={alloc.reviewLink}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      size="small"
                                      sx={{ p: 0.25 }}
                                    >
                                      <OpenInNew sx={{ fontSize: 14 }} />
                                    </IconButton>
                                  </Tooltip>
                                )}
                              </Box>
                            );
                          })}
                        </Box>
                      ) : (
                        <Typography variant="caption" color="text.secondary">
                          -
                        </Typography>
                      )}
                    </TableCell>
                  )}

                  {/* Allocations: who is reviewing this student's submission */}
                  {showAllocations && (
                    <TableCell>
                      {reviewedByAllocations.length > 0 ? (
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 0.5,
                          }}
                        >
                          {reviewedByAllocations.map((alloc) => (
                            <Chip
                              key={alloc.id}
                              icon={
                                alloc.reviewSubmitted ? (
                                  <CheckCircle fontSize="small" />
                                ) : (
                                  <RateReview fontSize="small" />
                                )
                              }
                              label={alloc.reviewerName}
                              size="small"
                              color={
                                alloc.reviewSubmitted ? "success" : "default"
                              }
                              variant="outlined"
                            />
                          ))}
                        </Box>
                      ) : (
                        <Typography variant="caption" color="text.secondary">
                          -
                        </Typography>
                      )}
                    </TableCell>
                  )}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </CardContent>

      {/* Submission detail modal */}
      {selectedSubmission && (
        <SubmissionDetail
          open={true}
          onClose={() => setSelectedSubmission(null)}
          submission={selectedSubmission}
          activity={activity}
          onRefresh={onRefresh ?? (() => {})}
        />
      )}
    </Card>
  );
}
